import React from 'react';

import DemmiCardDivider from '@demmi-ui/Card/CardDivider';
import DemmiInput from '@demmi-ui/Input';
import DemmiSelect from '@demmi-ui/Select/Select';
import { SelectItem } from '@demmi-ui/Select/_types';
import { DemmiFS } from '@subhanhabib/demmilib';

interface Props {
  currencyCode: string;
  name: string;
  setName: React.Dispatch<React.SetStateAction<string>>;
  minQuantity: number;
  setMinQuantity: React.Dispatch<React.SetStateAction<number>>;
  availableQuantity: number;
  setAvailableQuantity: React.Dispatch<React.SetStateAction<number>>;
  pricePerUnit: number;
  setPricePerUnit: React.Dispatch<React.SetStateAction<number>>;
  increments: number;
  setIncrements: React.Dispatch<React.SetStateAction<number>>;
  unit: DemmiFS.UnitsHelper.WeightUnit;
  setUnit: React.Dispatch<React.SetStateAction<DemmiFS.UnitsHelper.WeightUnit>>;

  // if unit === "weight_each"
  weightPerItem: number | undefined;
  setWeightPerItem: React.Dispatch<React.SetStateAction<number | undefined>>;
  unitPerItem?: DemmiFS.UnitsHelper.WeightUnit | undefined;
  setUnitPerItem: React.Dispatch<
    React.SetStateAction<DemmiFS.UnitsHelper.WeightUnit | undefined>
  >;
}

const InventoryProductVariantInputs: React.FC<Props> = ({
  currencyCode,
  name,
  setName,
  minQuantity,
  setMinQuantity,
  availableQuantity,
  setAvailableQuantity,
  pricePerUnit,
  setPricePerUnit,
  increments,
  setIncrements,
  unit,
  setUnit,
  weightPerItem,
  setWeightPerItem,
  unitPerItem,
  setUnitPerItem,
}) => {
  const CSSBlock = 'product-variant-inputs';

  const weightToMenuItem = (
    unit: DemmiFS.UnitsHelper.WeightUnit,
  ): SelectItem<DemmiFS.UnitsHelper.WeightUnit> => {
    return {
      id: unit,
      text: DemmiFS.UnitsHelper.getWeightUnitText(unit),
      item: unit,
    };
  };

  const weightItems = Object.entries(
    Object.values(DemmiFS.UnitsHelper.WeightUnit),
  ).map(([_, unit]) => weightToMenuItem(unit));

  const getMenuContent = (toOmit: DemmiFS.UnitsHelper.WeightUnit[]) => {
    return {
      sections: [
        {
          items: weightItems.filter(item => !toOmit.includes(item.item)),
        },
      ],
    };
  };

  return (
    <div className={`${CSSBlock}`}>
      <DemmiInput
        type="text"
        placeholder="Variant Name"
        value={name}
        onChangeValue={val => setName(val)}
      />
      <DemmiCardDivider />
      <div className={`${CSSBlock}__details-row`}>
        <div className={`${CSSBlock}__details-item`}>
          <div className={`${CSSBlock}__details-title`}>
            Available
            <br />
            Quantity
          </div>
          <DemmiInput
            type="number"
            placeholder="Available Quantity"
            value={availableQuantity.toString()}
            onChangeValue={val => setAvailableQuantity(parseInt(val))}
          />
        </div>
        <div className={`${CSSBlock}__details-item`}>
          <div className={`${CSSBlock}__details-title`}>
            Minimum
            <br />
            Quantity
          </div>
          <DemmiInput
            type="number"
            placeholder="Minimum Quantity"
            value={minQuantity.toString()}
            onChangeValue={val => setMinQuantity(parseInt(val))}
          />
        </div>
        <div className={`${CSSBlock}__details-item`}>
          <div className={`${CSSBlock}__details-title`}>
            Quantity
            <br />
            Increments
          </div>
          <DemmiInput
            type="number"
            placeholder="Quantity Increments"
            value={increments.toString()}
            onChangeValue={val => setIncrements(parseFloat(val))}
          />
        </div>
      </div>
      <DemmiCardDivider />
      <div className={`${CSSBlock}__details-row`}>
        <div className={`${CSSBlock}__details-item`}>
          <div className={`${CSSBlock}__details-title`}>
            Price Per Unit ({currencyCode})
          </div>
          <DemmiInput
            type="number"
            placeholder="Price Per Unit"
            value={pricePerUnit.toString()}
            onChangeValue={val => setPricePerUnit(parseFloat(val))}
          />
        </div>
        <div className={`${CSSBlock}__details-item`}>
          <div className={`${CSSBlock}__details-title`}>Unit</div>
          <DemmiSelect
            content={getMenuContent([DemmiFS.UnitsHelper.WeightUnit.undefined])}
            value={unit}
            visibleValue={DemmiFS.UnitsHelper.getWeightUnitText(unit) ?? '-'}
            onChange={id => {
              const newUnit = weightItems.find(item => item.id === id)?.item;
              if (newUnit) setUnit(newUnit);
            }}
          />
        </div>
      </div>
      {unit === DemmiFS.UnitsHelper.WeightUnit.each && (
        <div className={`${CSSBlock}__details-row`}>
          <div className={`${CSSBlock}__details-item`}>
            <div className={`${CSSBlock}__details-title`}>Weight Per Item</div>
            <DemmiInput
              type="number"
              placeholder="e.g. 85"
              value={weightPerItem?.toString() ?? ''}
              onChangeValue={val => setWeightPerItem(parseFloat(val))}
            />
          </div>
          <div className={`${CSSBlock}__details-item`}>
            <div className={`${CSSBlock}__details-title`}>Unit Per Item</div>
            <DemmiSelect
              content={getMenuContent([
                DemmiFS.UnitsHelper.WeightUnit.undefined,
                DemmiFS.UnitsHelper.WeightUnit.each,
              ])}
              value={unitPerItem ?? '-'}
              visibleValue={
                (unitPerItem &&
                  DemmiFS.UnitsHelper.getWeightUnitText(unitPerItem)) ??
                '-'
              }
              onChange={id => {
                const newUnit = weightItems.find(item => item.id === id)?.item;
                if (newUnit) setUnitPerItem(newUnit);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryProductVariantInputs;
